import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export interface Budget {
  id: string;
  parishId: string;
  annee: number;
  statut: "BROUILLON" | "VALIDE" | "CLOTURE";
  totalRecettesPrevu?: number;
  totalDepensesPrevu?: number;
  totalRecettesReel?: number;
  totalDepensesReel?: number;
  currencyCode?: string;
  validePar?: string;
  valideAt?: string;
}

export interface FinanceStats {
  [key: string]: any;
}

export function useBudgets(
  parishId: string | null | undefined,
  opts: { annee?: number; statut?: string; page?: number; size?: number } = {},
) {
  const { annee, statut, page = 0, size = 50 } = opts;
  return useQuery({
    queryKey: ["budgets", parishId, annee, statut, page, size],
    enabled: !!parishId,
    queryFn: async () => {
      const params = new URLSearchParams({
        parishId: String(parishId),
        page: String(page),
        size: String(size),
      });
      if (annee) params.append("annee", String(annee));
      if (statut) params.append("statut", statut);
      const res = await apiRequest(
        "GET",
        `/api/micro/finance/budgets?${params.toString()}`,
      );
      const data = await res.json();
      const items: Budget[] = Array.isArray(data) ? data : data.items ?? [];
      return { items, total: data.total ?? items.length };
    },
  });
}

export function useFinanceStats(parishId: string | null | undefined) {
  return useQuery({
    queryKey: ["finance-stats", parishId],
    enabled: !!parishId,
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/micro/finance/stats?parishId=${parishId}`,
      );
      return (await res.json()) as FinanceStats;
    },
  });
}

// ---------------------------------------------------------------------------
// Dashboards agrégés finance-service (écritures comptables réelles)
// ---------------------------------------------------------------------------

export interface AccountAmount {
  accountCode: string;
  accountLabel: string;
  amount: number;
}

export interface TreasuryAccountBalance {
  id: string;
  label: string;
  balance: number;
}

export interface FinanceTimelinePoint {
  month: string; // "YYYY-MM-01" (agrégat mensuel)
  recettes: number;
  charges: number;
}

/** Miroir de ParishDashboardDto (finance-service, SP-3 axe D). */
export interface ParishFinanceDashboard {
  parishId: string;
  from: string;
  to: string;
  recettesTotal: number;
  chargesTotal: number;
  netResult: number;
  piecesCount: number;
  digitalCount: number;
  bankRecHealthPct: number;
  recettesByAccount: AccountAmount[];
  chargesByAccount: AccountAmount[];
  treasuryByAccount: TreasuryAccountBalance[];
  timeline: FinanceTimelinePoint[];
  upcomingDueCount: number;
}

/**
 * Agrégats financiers réels d'une paroisse (recettes/charges/net, ventilation
 * par compte, trésorerie, série mensuelle). Sans `from`/`to`, le backend
 * agrège l'année civile en cours (1er janvier → aujourd'hui).
 */
export function useParishFinanceDashboard(
  parishId: string | null | undefined,
  opts: { from?: string; to?: string } = {},
) {
  return useQuery({
    queryKey: ["finance-dashboard", parishId, opts.from, opts.to],
    enabled: !!parishId,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts.from) params.append("from", opts.from);
      if (opts.to) params.append("to", opts.to);
      const qs = params.toString();
      const res = await apiRequest(
        "GET",
        `/api/micro/finance/dashboards/parish/${parishId}${qs ? `?${qs}` : ""}`,
      );
      return (await res.json()) as ParishFinanceDashboard;
    },
  });
}

export interface FinanceBenchmarkRow {
  parishId: string;
  recettes: number;
  depenses: number;
  net: number;
  nbWeeks: number;
}

/**
 * Benchmarking diocésain (une ligne par paroisse ayant ≥ 5 écritures sur
 * l'année). Endpoint réservé aux rôles admin diocésains : pour un rôle
 * paroisse (403), on retourne un tableau vide — les pages affichent alors un
 * état vide honnête au lieu de polluer le bandeau d'erreur global.
 */
export function useFinanceBenchmarking(opts: { year?: number } = {}) {
  return useQuery({
    queryKey: ["finance-benchmarking", opts.year],
    queryFn: async () => {
      try {
        const qs = opts.year ? `?year=${opts.year}` : "";
        const res = await apiRequest(
          "GET",
          `/api/micro/finance/dashboards/benchmarking${qs}`,
        );
        const data = await res.json();
        return (Array.isArray(data) ? data : []) as FinanceBenchmarkRow[];
      } catch {
        return [] as FinanceBenchmarkRow[];
      }
    },
  });
}

export interface BudgetLigne {
  id: string;
  budgetId: string;
  type: "RECETTE" | "DEPENSE";
  categorie: string;
  libelle?: string;
  montantPrevu: number;
  montantReel?: number;
  notes?: string;
}

/**
 * Lignes d'un budget. Utilisée par p75 Comptabilité (option A : pas de
 * plan comptable formel, on présente la grille recettes/dépenses du
 * budget courant comme tableau d'écritures simplifiées).
 */
export function useFinanceLignes(budgetId: string | null | undefined) {
  return useQuery({
    queryKey: ["finance-lignes", budgetId],
    enabled: !!budgetId,
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/micro/finance/budgets/${budgetId}/lignes`,
      );
      const data = await res.json();
      return (Array.isArray(data) ? data : data.items ?? []) as BudgetLigne[];
    },
  });
}

export function useCloreBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (budgetId: string) => {
      const res = await apiRequest(
        "POST",
        `/api/micro/finance/budgets/${budgetId}/cloturer`,
      );
      return (await res.json()) as Budget;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["budgets"] });
    },
  });
}

// ---- Saisie physique (recettes reçues au presbytère) ----------------------
// Décision PO du 06/10/2026 : tout est reversé à l'Économat ; chaque saisie
// crée une obligation visible dans « Mes reversements » et délivre un reçu.

export interface RecettePhysique {
  id: string;
  parishId: string;
  causeSlug: string;
  causeLibelle: string;
  mode: "ESPECES" | "CHEQUE";
  numeroCheque?: string | null;
  montant: number;
  libelle?: string | null;
  donateurNom?: string | null;
  donateurTelephone?: string | null;
  donateurEmail?: string | null;
  recuNumero: string;
  recuEmailEnvoyeLe?: string | null;
  recuSmsEnvoyeLe?: string | null;
  recuEnvoyeCourriel?: boolean;
  recuEnvoyeSms?: boolean;
  saisiParNom: string;
  saisiLe: string;
  dateFait: string;
  statut: "ENREGISTREE" | "ANNULEE";
  statutReversement?: "ATTENDU" | "PARTIEL" | "SOLDE" | "ANNULE" | null;
  montantDu?: number;
  dateEcheance?: string | null;
}

export interface NouvelleRecettePhysique {
  parishId: string;
  causeSlug: string;
  causeLibelle?: string;
  mode: "ESPECES" | "CHEQUE";
  numeroCheque?: string;
  montant: number;
  libelle?: string;
  donateurNom?: string;
  donateurTelephone?: string;
  donateurEmail?: string;
  /** Jour de la recette (AAAA-MM-JJ) dans une semaine pas encore clôturée ; absent : aujourd'hui. */
  dateFait?: string;
}

export interface Motif {
  id: string;
  slug: string;
  displayName: string;
}

/** Période d'une lecture de saisies (dates ISO AAAA-MM-JJ, bornes incluses). */
export interface PeriodeSaisies {
  du: string;
  au: string;
}

/** Intervalle de relecture des saisies écrites depuis un autre poste. */
export const RELECTURE_SAISIES_MS = 60_000;

/**
 * Lectures qui croisent plusieurs personnes (la secrétaire saisit, le curé
 * annule, chacun sur son poste) : `invaliderRecettes` ne joue que dans le
 * navigateur qui écrit, et le staleTime infini par défaut les figerait
 * jusqu'au F5.
 */
export interface OptionsLectureSaisies {
  /** Relue à chaque montage de l'écran. */
  relireAuMontage?: boolean;
  /** Relue toutes les minutes, tant que l'onglet est visible. */
  relireChaqueMinute?: boolean;
}

function optionsDeRelecture(options: OptionsLectureSaisies) {
  return {
    ...(options.relireAuMontage ? { refetchOnMount: "always" as const } : {}),
    ...(options.relireChaqueMinute ? { refetchInterval: RELECTURE_SAISIES_MS } : {}),
  };
}

/**
 * Saisies de la paroisse. Sans période : le mois en cours (choix du serveur,
 * bloc « Saisies au presbytère » du cockpit). Avec période : le jour du
 * guichet (page 91) ou la semaine de la clôture.
 */
export function useRecettesPhysiques(
  parishId: string | null | undefined,
  periode?: PeriodeSaisies,
  options: OptionsLectureSaisies = {},
) {
  return useQuery({
    queryKey: periode
      ? ["recettes-physiques", parishId, periode.du, periode.au]
      : ["recettes-physiques", parishId],
    enabled: !!parishId,
    ...optionsDeRelecture(options),
    queryFn: async () => {
      const params = new URLSearchParams({ parishId: String(parishId) });
      if (periode) {
        params.append("du", periode.du);
        params.append("au", periode.au);
      }
      const res = await apiRequest(
        "GET",
        `/api/micro/finance/recettes-physiques?${params.toString()}`,
      );
      const data = await res.json();
      return {
        items: (data?.items ?? []) as RecettePhysique[],
        total: Number(data?.total ?? 0),
      };
    },
  });
}

// ---- Guichet de la secrétaire (C2 du 19/10/2026, page 91) ------------------

/**
 * Un motif du guichet : liste fermée servie par finance-service
 * (`MotifsGuichet`), jamais ajoutée aux causes que voient les fidèles.
 * `casuel` : le motif demande sa précision (baptême, mariage, funérailles ou
 * autre).
 */
export interface MotifGuichet {
  slug: string;
  displayName: string;
  famille?: string | null;
  casuel: boolean;
}

/**
 * Motifs servis au guichet mais saisis ailleurs : l'offrande d'une intention
 * de messe passe par « Intentions de messe » (action 28), seul endroit où le
 * serveur l'accepte encore de la secrétaire. finance-service le marque
 * `reserveIntentions: true` ; le slug et la famille servent de filet.
 */
const MOTIFS_HORS_GUICHET = new Set(["messe"]);
const FAMILLES_HORS_GUICHET = new Set(["messe", "intention"]);

/**
 * Lit la réponse de `GET /recettes-physiques/motifs-guichet`. Contrat de
 * finance-service (`RecettePhysiqueResource.motifsGuichet`, une Map à cause
 * des pièges du natif) : `{ items: [{ slug, libelle, libelleRecu, famille,
 * nature, propreAuGuichet, reserveIntentions, codesCabinet }], total }`,
 * familles en majuscules (OFFRANDE, CONTRIBUTION, DON, CASUEL, VENTE,
 * CATECHESE, LOCATION, INTENTION). Un tableau nu ou `{ motifs }` se lit
 * aussi. Rien n'est ajouté : un motif sans slug ni libellé est écarté.
 */
export function normaliserMotifsGuichet(data: unknown): MotifGuichet[] {
  const d = data as { motifs?: unknown; items?: unknown } | unknown[] | null | undefined;
  const brut = Array.isArray(d) ? d : (d?.items ?? d?.motifs ?? []);
  if (!Array.isArray(brut)) return [];
  return (brut as Array<Record<string, unknown> | null>)
    .filter((m) => m?.reserveIntentions !== true)
    .map((m) => {
      const slug = String(m?.slug ?? "").trim();
      const famille = m?.famille == null ? null : String(m.famille);
      return {
        slug,
        displayName: String(m?.libelle ?? m?.displayName ?? m?.label ?? "").trim(),
        famille,
        casuel: m?.casuel === true || (famille ?? "").toLowerCase() === "casuel" || slug === "sacrements",
      };
    })
    .filter((m) => !!m.slug && !!m.displayName && !MOTIFS_HORS_GUICHET.has(m.slug)
      && !FAMILLES_HORS_GUICHET.has((m.famille ?? "").toLowerCase()));
}

export function useMotifsGuichet(parishId: string | null | undefined) {
  return useQuery({
    queryKey: ["motifs-guichet", parishId],
    enabled: !!parishId,
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/micro/finance/recettes-physiques/motifs-guichet?parishId=${parishId}`,
      );
      return normaliserMotifsGuichet(await res.json());
    },
  });
}

/** Mes encaissements : ce que la personne connectée a saisi sur la période. */
export interface MesEncaissements {
  items: RecettePhysique[];
  /** Total hors saisies annulées : la somme à remettre. */
  total: number;
  nombre: number;
}

export function useMesEncaissements(
  parishId: string | null | undefined,
  periode: PeriodeSaisies,
  options: OptionsLectureSaisies = {},
) {
  return useQuery<MesEncaissements>({
    queryKey: ["recettes-physiques-miennes", parishId, periode.du, periode.au],
    enabled: !!parishId,
    ...optionsDeRelecture(options),
    queryFn: async () => {
      const params = new URLSearchParams({ parishId: String(parishId), du: periode.du, au: periode.au });
      const res = await apiRequest(
        "GET",
        `/api/micro/finance/recettes-physiques/miennes?${params.toString()}`,
      );
      const data = await res.json();
      const items = (data?.items ?? []) as RecettePhysique[];
      const totalCalcule = items
        .filter((r) => r.statut !== "ANNULEE")
        .reduce((s, r) => s + Number(r.montant ?? 0), 0);
      return {
        items,
        total: data?.total == null ? totalCalcule : Number(data.total),
        nombre: data?.nombre == null ? items.length : Number(data.nombre),
      };
    },
  });
}

// ----- Clôture de la semaine : cumul par motif (06/10/2026) -----

export interface ClotureOrigine {
  count: number;
  total: number;
  /** Offrandes seulement : nombre encore à valider par le curé. */
  enAttente?: number;
  /** Offrandes seulement : montant encore à valider (20/10/2026). */
  montantEnAttente?: number;
  /**
   * Offrandes seulement : corrections (contre-écritures) comptées dans
   * `count`, et dans `enAttente` (finance-service, 22/10/2026). Absentes d'un
   * service plus ancien : tout se lit comme des offrandes.
   */
  corrections?: number;
  correctionsEnAttente?: number;
}

/** Une ligne de la clôture : un motif, trois origines. */
export interface ClotureLigne {
  motif: string;
  cle: string;
  nature: string;
  natureLibelle?: string | null;
  compteProduit?: string | null;
  quetes: ClotureOrigine;
  presbytere: ClotureOrigine;
  electronique: ClotureOrigine;
  totalCollecte: number;
  aVerser: number;
}

export interface ClotureSemaine {
  parishId: string;
  du: string;
  au: string;
  lignes: ClotureLigne[];
  totaux: {
    quetes: number;
    presbytere: number;
    electronique: number;
    collecte: number;
    aVerser: number;
    nombreQuetes: number;
    nombrePresbytere: number;
    nombreElectronique: number;
    /** Lignes des messes encore à valider par le curé, corrections comprises. */
    quetesEnAttente: number;
    /** Dont corrections (contre-écritures) : D1, 22/10/2026. */
    correctionsEnAttente?: number;
    /** Leur montant (finance-service, 20/10/2026). */
    quetesEnAttenteMontant?: number;
    /** Part validée du montant à verser : la seule que l'Économat réclame (D-E). */
    aVerserCertifie?: number;
  };
}

/**
 * Tout ce que la paroisse a collecté sur la période, par motif : dons des
 * messes, saisies au presbytère et dons de l'application. Les deux premières
 * origines sont à verser à 100 % à l'Économat ; la troisième est déjà sur le
 * compte du diocèse et n'apparaît que pour information.
 */
export function useClotureSemaine(
  parishId: string | null | undefined,
  periode: { from: string; to: string },
) {
  return useQuery<ClotureSemaine>({
    queryKey: ["cloture-semaine", parishId, periode.from, periode.to],
    enabled: !!parishId,
    queryFn: async () => {
      const params = new URLSearchParams({ parishId: String(parishId), du: periode.from, au: periode.to });
      const res = await apiRequest("GET", `/api/micro/finance/cloture-semaine?${params}`);
      return (await res.json()) as ClotureSemaine;
    },
  });
}

/** Motifs de la liste de la paroisse (mêmes que les dons de l'application). */
/**
 * Un motif par libellé. Le catalogue peut porter deux causes de même nom
 * (STAGING, 10/10/2026 : « Offrande » sous les slugs `quete` et `offrande`) :
 * deux tuiles « Offrande » choisies ensemble, et des dons ventilés sur deux
 * motifs (défaut D034). On garde la PREMIÈRE, celle que le catalogue classe
 * devant (priorité croissante : `quete`, le motif historique des dons des
 * messes). Libellés comparés sans casse, accents ni espaces superflus.
 */
export function dedoublonnerMotifs(liste: Motif[]): Motif[] {
  const vus = new Set<string>();
  return liste.filter((m) => {
    const cle = m.displayName
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
    if (vus.has(cle)) return false;
    vus.add(cle);
    return true;
  });
}

export function useMotifs() {
  return useQuery({
    queryKey: ["motifs-dons"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/micro/causes");
      const data = await res.json();
      const liste = (data?.causes ?? data?.items ?? (Array.isArray(data) ? data : [])) as Motif[];
      return dedoublonnerMotifs(liste.filter((m) => m.slug && m.displayName));
    },
  });
}

function invaliderRecettes(qc: ReturnType<typeof useQueryClient>, parishId: string) {
  // staleTime: Infinity par défaut : chaque clé lue après une saisie ou une
  // annulation doit être invalidée ici, sinon l'écran reste figé. Le préfixe
  // couvre aussi les lectures par période (guichet du jour, semaine de la
  // clôture).
  qc.invalidateQueries({ queryKey: ["recettes-physiques", parishId] });
  qc.invalidateQueries({ queryKey: ["recettes-physiques-miennes", parishId] });
  qc.invalidateQueries({ queryKey: ["cloture-semaine", parishId] });
  qc.invalidateQueries({ queryKey: [`/api/micro/reversements/obligations?parishId=${parishId}`] });
  qc.invalidateQueries({ queryKey: [`/api/micro/reversements/compte/${parishId}`] });
}

export function useCreerRecettePhysique() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: NouvelleRecettePhysique) => {
      const res = await apiRequest("POST", "/api/micro/finance/recettes-physiques", body);
      return (await res.json()) as RecettePhysique;
    },
    onSuccess: (_r, body) => invaliderRecettes(qc, body.parishId),
  });
}

export function useAnnulerRecettePhysique(parishId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, motif }: { id: string; motif?: string }) => {
      const res = await apiRequest(
        "POST",
        `/api/micro/finance/recettes-physiques/${id}/annuler`,
        { motif },
      );
      return (await res.json()) as RecettePhysique;
    },
    onSuccess: () => {
      if (parishId) invaliderRecettes(qc, parishId);
    },
  });
}

/** Ouvre le reçu PDF dans un nouvel onglet (la requête porte le jeton de session). */
export async function ouvrirRecuPdf(id: string, duplicata: boolean) {
  const fenetre = window.open("", "_blank");
  try {
    const res = await apiRequest(
      "GET",
      `/api/micro/finance/recettes-physiques/${id}/pdf${duplicata ? "?duplicata=true" : ""}`,
    );
    const url = URL.createObjectURL(await res.blob());
    if (fenetre) {
      fenetre.location.href = url;
    } else {
      window.location.href = url;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (erreur) {
    // Pas d'onglet blanc laissé ouvert : l'appelant dit l'échec à l'écran.
    try {
      fenetre?.close();
    } catch {
      /* onglet déjà fermé */
    }
    throw erreur;
  }
}
